/* eslint-disable @typescript-eslint/no-empty-function */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { Card, Heading, Icon, Modal, Text, TextButton } from '@amzn/storm-ui';

import Fade from '../Transitions/Fade';
import Element from '../Element';
import { Carousel, CarouselSlide } from '../Carousel';

const CarouselSlideShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  caption: PropTypes.string,
  media: PropTypes.shape({
    link: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['image', 'video', 'IMAGE', 'VIDEO']),
    altText: PropTypes.string.isRequired,
  }).isRequired,
});

const fadeInFrames = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeIn = css`
  animation: ${fadeInFrames} 1s;
`;

const CarouselModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: -${({ theme }) => theme.spacing.base.default} -${({ theme }) =>
      theme.spacing.xsmall.default};
`;
CarouselModalContainer.displayName = 'CarouselModalContainer';

const CarouselContainer = styled.div`
  flex: 1 1 auto;
`;

const Headline = styled(Heading)`
  margin: 30px;
  text-align: center;
  ${fadeIn};
`;
Headline.displayName = 'Headline';

const Caption = styled(Text)`
  text-align: center;
`;
Caption.displayName = 'Caption';

const SlideHeadline = styled(Heading)`
  color: ${({ theme }) => theme.color.gray[0]};
  margin-bottom: 20px;
`;
SlideHeadline.displayName = 'SlideHeadline';

const SlideCaption = styled(Text)`
  color: ${({ theme }) => theme.color.gray[0]};
`;
SlideCaption.displayName = 'SlideCaption';

const SlideTextContainer = styled.div`
  width: 360px;
  align-self: center;
`;

const SlideMediaContainer = styled(Card)`
  min-width: 330px;
  min-height: 330px;
  max-width: 360px;
  align-self: center;
  margin: 0 0 0 22px;
`;

const CloseButton = styled(TextButton)`
  margin-left: 26px;
  border: none;
  background: transparent;
  padding: ${({ theme }) => theme.spacing.none.default};
  position: absolute;
  top: 12px;
  right: 22px;
`;
CloseButton.displayName = 'CloseButton';

const CloseIcon = styled(Icon)`
  font-size: 20px;
  padding: 0 ${({ theme }) => theme.spacing.xxxsmall.default};
  color: ${({ theme }) => theme.color.gray[0]};

  &:hover {
    cursor: pointer;
  }
`;
CloseIcon.displayName = 'CloseIcon';

const HeaderContainer = styled.div`
  background-color: ${({ theme }) => theme.color.gray[800]};
  color: ${({ theme }) => theme.color.gray[0]};
`;

export default class CarouselModal extends PureComponent {
  render() {
    const {
      caption,
      headline,
      id,
      isOpen,
      logo,
      onClose,
      closeButtonText,
      nextButtonText,
      previousButtonText,
      playButtonText,
      pauseButtonText,
      steps,
      toggleRef,
    } = this.props;

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        hasCloseButton={false}
        toggleEl={toggleRef}
        modalElementId="AACChromePortal"
        contentClassName="modal--padding-none"
      >
        <CarouselModalContainer>
          <CloseButton
            onClick={onClose}
            aria-label={closeButtonText}
            data-ccx-e2e-id="aac-ftu-modal-close"
          >
            <CloseIcon type="close" />
          </CloseButton>
          <HeaderContainer>
            <Fade in timeout={300}>
              <Headline renderAs="h1" color="primary">
                {headline}
              </Headline>
            </Fade>
          </HeaderContainer>
          <CarouselContainer>
            <Carousel
              autoPlay
              ariaLabels={{
                previous: previousButtonText,
                next: nextButtonText,
                play: playButtonText,
                pause: pauseButtonText,
                progressPillAriaLabels: steps.map((slide) => slide.headline),
              }}
              data-takt-id={`aac-nav-modal-${id}`}
            >
              {steps.map((slide) => (
                <CarouselSlide key={slide.id} duration={slide.duration}>
                  <SlideTextContainer>
                    <SlideHeadline renderAs="h2">
                      {slide.headline}
                    </SlideHeadline>
                    <SlideCaption size="large">{slide.caption}</SlideCaption>
                  </SlideTextContainer>
                  <SlideMediaContainer padding="small">
                    {slide.media?.type?.toLowerCase() === 'image' && (
                      <Element
                        type="img"
                        alt={slide.media.altText}
                        src={slide.media.link}
                        width="100%"
                        height="100%"
                        maxHeight="360px"
                      />
                    )}
                    {slide.media?.type?.toLowerCase() === 'video' && (
                      <Element
                        type="video"
                        width="100%"
                        height="100%"
                        autoPlay
                        muted
                        loop
                      >
                        <source src={slide.media.link} type="video/mp4" />
                      </Element>
                    )}
                  </SlideMediaContainer>
                </CarouselSlide>
              ))}
            </Carousel>
          </CarouselContainer>
        </CarouselModalContainer>
      </Modal>
    );
  }
}

CarouselModal.propTypes = {
  id: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  toggleRef: PropTypes.instanceOf(
    typeof Element !== 'undefined' ? Element : Object,
  ),
  headline: PropTypes.string.isRequired,
  caption: PropTypes.string,
  closeButtonText: PropTypes.string,
  previousButtonText: PropTypes.string,
  nextButtonText: PropTypes.string,
  playButtonText: PropTypes.string,
  pauseButtonText: PropTypes.string,
  logo: PropTypes.shape({
    image: PropTypes.string.isRequired,
    altText: PropTypes.string.isRequired,
  }).isRequired,
  steps: PropTypes.arrayOf(CarouselSlideShape).isRequired,
};

CarouselModal.defaultProps = {
  isOpen: false,
  onClose: () => {},
  toggleRef: undefined,
  caption: '',
  closeButtonText: '',
  previousButtonText: '',
  nextButtonText: '',
  playButtonText: '',
  pauseButtonText: '',
};
